import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Trash2, KeyRound } from "lucide-react";

export function AdminsModule() {
  const { admin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addUser = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/users", newUser);
      toast.success("Admin user added");
      setNewUser({ email: "", name: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Admin disabled" : "Admin enabled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email} (min 8 characters):`);
    if (!pw) return;
    try {
      await api.put(`/admin/users/${u.id}`, { password: pw });
      toast.success("Password reset");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete admin "${u.email}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Admin deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/change-password", pwForm);
      toast.success("Your password has been changed");
      setPwForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-module-users" className="max-w-3xl space-y-8">
      <div>
        <h2 className="font-display text-2xl font-bold text-white">Admin Users</h2>
        <p className="text-sm text-slate-400">Manage who can access this dashboard. Passwords are securely hashed.</p>
      </div>

      <div className="glass rounded-xl p-6" data-testid="admin-users-list">
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase mb-4">Current Admins</p>
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-7 h-7 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            {users.map((u) => (
              <div key={u.id} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-white/10 px-4 py-3" data-testid={`admin-user-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white text-sm truncate">{u.name} {u.id === admin?.id && <span className="text-[10px] text-[#00B0FF] font-mono-tech">(YOU)</span>}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-[10px] font-mono-tech ${u.is_active ? "text-[#00E676]" : "text-red-400"}`}>
                    {u.is_active ? "ACTIVE" : "DISABLED"}
                  </span>
                  <Switch data-testid={`admin-user-toggle-${u.id}`} checked={u.is_active} onCheckedChange={() => toggleActive(u)} disabled={u.id === admin?.id} />
                  <Button size="icon" variant="outline" data-testid={`admin-user-reset-${u.id}`} onClick={() => resetPassword(u)} className="border-white/15 text-slate-300 hover:bg-white/5" title="Reset password">
                    <KeyRound className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="outline" data-testid={`admin-user-delete-${u.id}`} onClick={() => remove(u)} disabled={u.id === admin?.id} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={addUser} className="glass rounded-xl p-6 space-y-4" data-testid="admin-add-user-form">
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase flex items-center gap-2">
          <UserPlus className="w-3.5 h-3.5" /> Add Admin User
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Email *</Label>
            <Input data-testid="admin-new-email" type="email" required value={newUser.email} onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))} className="bg-[#06080E] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Name</Label>
            <Input data-testid="admin-new-name" value={newUser.name} onChange={(e) => setNewUser((p) => ({ ...p, name: e.target.value }))} className="bg-[#06080E] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Password * (min 8)</Label>
            <Input data-testid="admin-new-password" type="password" required minLength={8} value={newUser.password} onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))} className="bg-[#06080E] border-white/10" />
          </div>
        </div>
        <Button type="submit" disabled={adding} data-testid="admin-add-user-submit" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          {adding ? "Adding..." : "Add Admin"}
        </Button>
      </form>

      <form onSubmit={changePassword} className="glass rounded-xl p-6 space-y-4" data-testid="admin-change-password-form">
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase">Change My Password</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Current Password</Label>
            <Input data-testid="admin-current-password" type="password" required value={pwForm.current_password} onChange={(e) => setPwForm((p) => ({ ...p, current_password: e.target.value }))} className="bg-[#06080E] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">New Password (min 8)</Label>
            <Input data-testid="admin-new-pw" type="password" required minLength={8} value={pwForm.new_password} onChange={(e) => setPwForm((p) => ({ ...p, new_password: e.target.value }))} className="bg-[#06080E] border-white/10" />
          </div>
        </div>
        <Button type="submit" data-testid="admin-change-password-submit" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          Change Password
        </Button>
      </form>
    </div>
  );
}
